import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../../services/api";
import "./subjects.css";

export default function AddSubject() {
  const school = JSON.parse(localStorage.getItem("schoolAdmin"));
  const navigate = useNavigate();

  const [classes, setClasses] = useState([]);
  const [sectionsMap, setSectionsMap] = useState({});
  const [expandedClass, setExpandedClass] = useState(null);
  const [subjectName, setSubjectName] = useState("");
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadClasses = async () => {
    const res = await API.get("/school-admin/classes/", {
      headers: { "X-SCHOOL-ID": school.school.id },
    });
    setClasses(res.data.classes || []);
  };

  const loadSections = async (classId) => {
    if (sectionsMap[classId]) return;
    const res = await API.get(`/school-admin/sections/${classId}/`, {
      headers: { "X-SCHOOL-ID": school.school.id },
    });
    setSectionsMap((prev) => ({ ...prev, [classId]: res.data.sections || [] }));
  };

  useEffect(() => { loadClasses(); }, []);

  const toggleSection = (classId, sectionId) => {
    const exists = selected.find((s) => s.class_id === classId && s.section_id === sectionId);
    setSelected(exists
      ? selected.filter((s) => !(s.class_id === classId && s.section_id === sectionId))
      : [...selected, { class_id: classId, section_id: sectionId }]
    );
  };

  const toggleClass = async (classId) => {
    await loadSections(classId);
    setExpandedClass(expandedClass === classId ? null : classId);
  };

  const selectAll = async () => {
    for (const c of classes) await loadSections(c.id);
    setTimeout(() => {
      const all = [];
      classes.forEach((c) => {
        (sectionsMap[c.id] || []).forEach((s) => all.push({ class_id: c.id, section_id: s.id }));
      });
      setSelected(all);
    }, 300);
  };

  const clearAll = () => setSelected([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subjectName.trim()) return alert("Enter subject name");
    if (selected.length === 0) return alert("Select at least one class/section");
    setLoading(true);
    try {
      await API.post("/school-admin/subjects/create/",
        { name: subjectName, selections: selected },
        { headers: { "X-SCHOOL-ID": school.school.id } }
      );
      navigate("/school-admin/subjects");
    } catch {
      alert("Error adding subject");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="asb-page">
      <div className="asb-card">

        {/* HEADER */}
        <div className="asb-card-header">
          <span className="asb-card-icon">📖</span>
          <div>
            <h2 className="asb-card-title">Add New Subject</h2>
            <p className="asb-card-sub">Name the subject then pick which classes & sections will have it</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>

          {/* SUBJECT NAME */}
          <div className="asb-field">
            <label className="asb-label">Subject Name</label>
            <input
              type="text"
              className="asb-input"
              placeholder="e.g. Mathematics, English Literature, Physics"
              value={subjectName}
              onChange={(e) => setSubjectName(e.target.value)}
              autoFocus
            />
          </div>

          {/* SELECTION HEADER */}
          <div className="asb-sel-hdr">
            <div>
              <p className="asb-sel-title">Assign to Classes & Sections</p>
              <p className="asb-sel-count">
                {selected.length === 0 ? "None selected" : `${selected.length} section${selected.length > 1 ? "s" : ""} selected`}
              </p>
            </div>
            <div className="asb-sel-btns">
              <button type="button" className="asb-btn-selall" onClick={selectAll}>Select All</button>
              {selected.length > 0 && (
                <button type="button" className="asb-btn-clear" onClick={clearAll}>Clear</button>
              )}
            </div>
          </div>

          {/* CLASS ACCORDIONS */}
          <div className="asb-classes">
            {classes.map((c) => {
              const isOpen = expandedClass === c.id;
              const secs = sectionsMap[c.id] || [];
              const selectedCount = selected.filter((s) => s.class_id === c.id).length;
              return (
                <div key={c.id} className={`asb-class-block${isOpen ? " open" : ""}`}>
                  <div className="asb-class-hdr" onClick={() => toggleClass(c.id)}>
                    <span className="asb-class-name">🏛️ {c.name}</span>
                    <div className="asb-class-right">
                      {selectedCount > 0 && (
                        <span className="asb-sel-badge">{selectedCount} selected</span>
                      )}
                      <span className="asb-arrow">{isOpen ? "▾" : "▸"}</span>
                    </div>
                  </div>
                  {isOpen && (
                    <div className="asb-sections">
                      {secs.length === 0 ? (
                        <p className="asb-no-sec">No sections found for this class</p>
                      ) : (
                        secs.map((s) => {
                          const checked = selected.some(
                            (sel) => sel.class_id === c.id && sel.section_id === s.id
                          );
                          return (
                            <label key={s.id} className={`asb-sec-item${checked ? " checked" : ""}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleSection(c.id, s.id)}
                              />
                              <span className="asb-sec-name">Section {s.name}</span>
                              {checked && <span className="asb-check-icon">✓</span>}
                            </label>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* SUBMIT */}
          <div className="asb-btn-row">
            <button type="button" className="asb-btn-back" onClick={() => navigate("/school-admin/subjects")}>
              ← Back
            </button>
            <button type="submit" className="asb-btn-submit" disabled={loading}>
              {loading ? "Adding…" : `+ Add Subject${selected.length > 0 ? ` (${selected.length})` : ""}`}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}
